<template>
  <ui-route-aware-sidebar-container
    :id="LEAVE_SERVICE_DETAILS_MODAL_ID"
    :fallback-route="parentRoute"
    :size="UI.SIZE.SMALL"
    placement="end"
    body-class="px-0"
    fit-workspace
  >
    <template #title>
      <ui-cloak
        :is-visible="fetchState.status.value === 'success'"
        :idle-phase-duration="300"
        :sustain-phase-duration="300"
      >
        {{ details.name }}
      </ui-cloak>
    </template>
    <template #default>
      <ui-cloak
        :is-visible="fetchState.status.value === 'success'"
        :idle-phase-duration="300"
        :sustain-phase-duration="300"
      >
        <template #cloak>
          <div class="page-details__preloading">
            <ui-spinner
              :size="UI.SIZE.LARGE"
            />
          </div>
        </template>

        <template #default>
          <ui-wad
            class="animate__animated animate__fadeIn ease-in-out a1"
          >

            <div class="page-details__main">

              <!-- Details -->
              <ui-reset ref="reset_detailsForm">
                <details-form
                  ref="detailsForm"
                  v-bind="{
                    submitCallback: updateCallback,
                    searchApproverCallback,
                    isPending: onDetailsPending,
                  }"
                  :default-data="details"
                  @cancel="() => ref_reset_detailsForm?.reset()"
                  @on-approver-selection="onApproverSelection"
                />
              </ui-reset>

              <hr class="page-details__separator">

              <!-- Members -->
              <ui-wad>

                <ui-caveat
                  v-if="!areRttAvailable
                  "
                  :size="UI.SIZE.SMALL"
                  :variant="UI.VARIANT.LIGHT"
                  :aside-variant="UI.VARIANT.LIGHT"
                  class="mb-3"
                >
                  <template #aside>
                    <ui-icon
                      name="settings"
                      :theme="UI.ICON_THEME.FILLED"
                    />
                  </template>
                  <template #default>
                    <p class="mb-0.5">
                      Aucun droit RTT n'est paramétré pour la structure
                    </p>
                    <p class="font-weight-light font-size-95 color-gray-deep">
                      Si vous souhaitez le paramétrer, vous pouvez le faire en allant dans le
                    <ui-link
                      underline="hover"
                      class="color-secondary"
                      :route="parametersRoute"
                    >
                      menu Paramètres
                    </ui-link>
                    </p>
                  </template>
                </ui-caveat>

                <b-card
                  :class="[
                    'page-details__members',
                    'card--plain'
                  ]"
                >
                  <template #header>

                    <ui-wad class="d-flex gap-6 align-items-center justify-content-space-between">

                      <ui-blank
                        v-if="collaboratorCount === 0"
                      />

                      <span
                        v-if="collaboratorCount > 0"
                      >
                        {{ collaboratorCount }}
                        {{ collaboratorCount > 1 ? `salariés` : `salarié` }}
                      </span>

                      <ui-tooltip-wrapper
                        v-hide="isCollaboratorInvitationDisplayed"
                        class="d-inline-block"
                        v-bind="{
                          title: `Vous n'avez pas les droits pour effectuer cette action`,
                          variant: UI.VARIANT.LIGHT,
                        }"
                        :status="hasACL(ACLS.LEAVE_ENABLE_TEAM_MEMBER) ?
                          UI.STATUS.DISABLED :
                          UI.STATUS.ENABLED
                        "
                      >
                        <ui-button
                          data-cy="add_member"
                          :variant="UI.VARIANT.PRIMARY"
                          :size="UI.SIZE.SMALL"
                          :status="hasACL(ACLS.LEAVE_ENABLE_TEAM_MEMBER) ?
                            UI.STATUS.ENABLED :
                            UI.STATUS.DISABLED
                          "
                          outline
                          @click="() => showCollaboratorInvitation()"
                        >
                          <ui-icon
                            name="person_add"
                          />
                          &nbsp;
                          Ajouter
                        </ui-button>
                      </ui-tooltip-wrapper>

                    </ui-wad>
                  </template>

                  <template #default>

                    <collaborator-invitation
                      v-if="isCollaboratorInvitationDisplayed"
                      ref="collaboratorInvitation"
                      v-bind="{
                        suggestionCallback: searchCompanyCollaboratorCallback,
                        submitCallback: inviteOneCollaboratorCallback,
                      }"
                      class="page-details__invitation"
                    />

                    <ui-caveat
                      v-if="collaboratorCount === 0"
                      class="text-align-center py-4"
                    >
                      <ui-icon
                        name="person_off"
                        class="d-block font-size-30px mb-0.5 color-gray-medium"
                        :theme="UI.ICON_THEME.FILLED"
                      />
                      <span class="d-block font-weight-light color-gray-medium">
                        Aucun salarié
                      </span>
                    </ui-caveat>

                    <collaborator-list
                      v-if="collaboratorCount > 0"
                      ref="collaboratorList"
                      class="page-details__list"
                      v-bind="{
                        deleteCallback: deleteOneCollaboratorCallback,
                        updateAcquiringCycleCallback,
                        items: collaboratorList,
                        withAcquiringCycle: areRttAvailable
                      }"
                    />

                  </template>
                </b-card>

              </ui-wad>

            </div>

          </ui-wad>
        </template>
      </ui-cloak>
    </template>
  </ui-route-aware-sidebar-container>
</template>

<style lang="sass" scoped>
@import '~/assets/sass/modules/layout/variables'
@import '~/assets/sass/modules/layout/members'

.page-details

  &__preloading
    text-align: center
    margin-top: 30vh

  &__separator
    height: 1px
    background-color: $border-color
    margin-bottom: $layout-spacer-y * .5

  ////////////////////////////////////

  &__members

    :deep(*)

      &.card-body
        padding-top: initial

  ////////////////////////////////////

  &__invitation
    // quick extrusion on parent card header padding
    margin-top: - ($card-spacer-y * .5)

    // quick extrusion on parent card padding
    margin-left: - $card-spacer-x
    margin-right: - $card-spacer-x

    margin-bottom: $card-spacer-y

  ////////////////////////////////////

  /// Animation
  ///
  /// Emphasize details form submit CTA for alterness affordance
  :deep(.ui-button--emphasized)
    $delay: 150ms // (defensive: modal closing)

    animation: rubberband-light 600ms ease-in-out
    animation-delay: $delay

</style>

<script lang="ts">
import DetailsForm from '~/components/transat/service/details/DetailsFormEdit.vue'
import CollaboratorInvitation from '~/components/transat/service/details/CollaboratorInvitation.vue'
import CollaboratorList from '~/components/transat/service/details/CollaboratorList.vue'
import Console from '~/utils/lib/core/console'
import { clone, delay, join, length, Maybe, noop, prop, sustainedPromise, tapCatch, toString } from '~/utils/lib/core/prelude'
import { getListRoute, type Service } from '~/stores/transat/service'
import { getMainRoute as getParametersRoute } from '~/stores/transat/parameters'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useLegacyModalController } from '~/composables/layout/modal'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { useAccessControl } from '~/composables/layout/access-control'
import type { ListInputSchema } from '~/utils/infra/common/types_'
import { useStore_transat_collaborator, type Collaborator } from '~/stores/transat/collaborator'
import { useStore_transat_service_details } from '~/stores/transat/service/details'
import { useStore_transat_service_collaborator } from '~/stores/transat/service/collaborator'
import { useStore_transat_approver_list } from '~/stores/transat/approver/list'
import { useStore_transat_collaborator_list } from '~/stores/transat/collaborator/list'
import type { Company } from '~/stores/client/access'
import type { UiReset } from '#components'
import { EVENTS } from '~/stores/transat/service/list'

const console = new Console('component:team-details')

const LEAVE_SERVICE_DETAILS_MODAL_ID = `leave-service-details-modal-id`

const collaboratorListInput :ListInputSchema = {
  per_page: 10000, // eager
  order: 'nom',
  sort: 'asc',
}

export default defineNuxtComponent({
  components: { CollaboratorInvitation, CollaboratorList, DetailsForm },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.ABSENCES,
      scrollPolicy: 'preserve-when-involved',
    })

    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: `Équipe`,
    })

    const triggeredParentListRefresh
      = inject(EVENTS.ON_SERVICE_LIST_REFRESH_NEEDED, noop)

    const parentRoute = getListRoute(
      navigationContext.fromCompanyLevel().structure.id,
      navigationContext.fromCompanyLevel().company.code_societe
    )

    const parametersRoute = getParametersRoute(
      navigationContext.fromCompanyLevel().structure.id,
      navigationContext.fromCompanyLevel().company.code_societe
    )

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const {
      hideModal,
      showModal,
    } = useLegacyModalController()

    const ToastBuilder = useToastBuilder()

    const ref_collaboratorInvitation
      = useTemplateRef<InstanceType<typeof CollaboratorInvitation>>('collaboratorInvitation')

    const ref_reset_detailsForm
      = useTemplateRef<InstanceType<typeof UiReset>>('reset_detailsForm')

    const ref_detailsForm
      = useTemplateRef<InstanceType<typeof DetailsForm>>('detailsForm')

    const store_service_details = useStore_transat_service_details()
    const store_service_collaborator = useStore_transat_service_collaborator()
    const store_approver_list = useStore_transat_approver_list()
    const store_collaborator_list = useStore_transat_collaborator_list()
    const store_collaborator = useStore_transat_collaborator()

    return {
      ...navigationContext,
      ...useAccessControl(),
      fetchState,
      fetchWrapper,
      hideModal,
      showModal,
      ToastBuilder,
      ref_collaboratorInvitation,
      ref_detailsForm,
      ref_reset_detailsForm,
      store_service_details,
      store_service_collaborator,
      store_approver_list,
      store_collaborator_list,
      store_collaborator,
      parentRoute,
      parametersRoute,
      triggeredParentListRefresh,
    }
  },
  data() {
    return {
      LEAVE_SERVICE_DETAILS_MODAL_ID,
      onDetailsPending: false,
      details: null as unknown as Service, // lazy
      collaboratorList: null as unknown as Array<Collaborator>, // lazy
      areRttAvailable: false, // lazy
      isCollaboratorInvitationDisplayed: false,
    }
  },
  computed: {
    collaboratorCount() {
      return length( this.collaboratorList )
    }
  },
  created() {
    this.fetchWrapper(
      () =>  {
        const uuidData = this.fromCompanyLevel().structure.uuidData
        const code_societe = this.fromCompanyLevel().company.code_societe
        const uuidTeam = this.$route.params.serviceId as string

        return Promise.all([
            this.store_service_details.read(
              { uuidData, code_societe, uuidTeam }
            ),
            this.store_service_collaborator.list(
              { uuidData, code_societe, uuidTeam, input: collaboratorListInput }
            )
          ])
          .then(results => {
            this.details = results[0]

            this.collaboratorList = results[1].data

            this.areRttAvailable = results[1].areRttAvailable

            setPageTitle(
              this.details.name,
              this.fromCompanyLevel().company.raison_sociale
            )
          })
          .catch(tapCatch(err => {
            console.warn(err)
            this.ToastBuilder
              .error(toString(err))
              .title(`Fiche équipe`)
              .show()
          }))
      }
    )
  },
  methods: {
    /**
     * @desc
     *    - update document page title accordingly
     *    - update header page title accordingly
     */
    updateCallback(data) {
      const opts = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        uuidTeam: this.$route.params.serviceId as string,
        data,
      }

      this.onDetailsPending = true

      return sustainedPromise(
          300,
          this.store_service_details.update(opts)
        )
        .then(res => {
          this.onDetailsPending = false

          this.ToastBuilder
            .success()
            .title(`Équipe modifiée`)
            .show()

          setPageTitle(
            res.name,
            this.fromCompanyLevel().company.raison_sociale
          )

          this.details = clone( res )

          this.ref_reset_detailsForm?.reset()

          this.triggeredParentListRefresh()
        })
        .catch(err => {
          this.onDetailsPending = false
          console.warn('updateCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Modification équipe`)
            .show()
        })
    },
    searchApproverCallback() {
      const opts = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return this.store_approver_list.read(opts)
        .catch(tapCatch(err => {
          console.warn('searchApproverCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche d'utilisateurs`)
            .show()
        }))
    },
    searchCompanyCollaboratorCallback(searchQuery) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        searchQuery,
      }

      return this.store_collaborator_list.search(options)
        .catch(tapCatch(err => {
          console.warn('searchCompanyCollaboratorCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche de salariés`)
            .show()
        }))
    },
    /**
     * @desc
     *    - renew member list on update
     */
    inviteOneCollaboratorCallback(matricule :Collaborator["matricule"]) {
      const opts = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        uuidTeam: this.$route.params.serviceId as string,
      }

      return this.store_service_collaborator.inviteOne(
          { ...opts, matricule }
        )
        // renew collaborator list
        .then(() => this.store_service_collaborator.list(
          { ...opts, input: collaboratorListInput }
        ))
        .then(result => {
          this.collaboratorList = prop('data', result)
          this.triggeredParentListRefresh()
        })
        .then(() => this.ToastBuilder
          .success()
          .title(`Ajout effectué !`)
          .show()
        )
        .catch(err => {
          console.warn('inviteMember', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Ajout de nouveaux membres`)
            .show()
        })
    },
    /**
     * @desc
     *    - renew member list on update (detached and delayed)
     */
    deleteOneCollaboratorCallback(matricule :Collaborator["matricule"]) {
      const opts = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        uuidTeam: this.$route.params.serviceId as string,
      }

      return this.store_service_collaborator.deleteOne(
          { ...opts, matricule }
        )
        // renew collaborator list (delayed, allowing internal deletion UI
        // feedback)
        .then(() => {
          delay(300)
            .then(
              () => this.store_service_collaborator.list(
                { ...opts, input: collaboratorListInput }
              )
            )
            .then(result => {
              this.collaboratorList = prop('data', result)
              this.triggeredParentListRefresh()
            })
            .catch(err => {
              console.warn('deleteMember:listUpdate', err)
              this.ToastBuilder
                .error(toString(err))
                .title(`Suppression membre`)
                .show()
            })
        })
        .catch(tapCatch(err => {
          console.warn('deleteMember', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Suppression membre`)
            .show()
        }))
    },
    async showCollaboratorInvitation() {
      this.isCollaboratorInvitationDisplayed = true

      await this.$nextTick()

      this.ref_collaboratorInvitation?.focus()
    },
    /**
     * @desc Emphasize submit CTA: needed affordance, otherwise user could be
     *    confused whether previous click on the approbator list selection had
     *    triggered form submit or not
     */
    onApproverSelection() {
      Maybe.toMaybe(this.ref_detailsForm)
        .chain(instance => Maybe.toMaybe(instance.ref_submitCTA))
        .chain(instance => instance.getElement())
        .map(async (element) => {
          element.classList.remove('ui-button--emphasized')
          await delay(0)
          element.classList.add('ui-button--emphasized')
        })
    },
    updateAcquiringCycleCallback(matricule, data) {
      const opts = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        uuidTeam: this.$route.params.serviceId as string,
        matricule,
        data
      }

      return sustainedPromise(
          300,
          this.store_collaborator.updateAcquiringCycle(opts)
        )
        .catch(tapCatch(err => {
          console.warn('updateAcquiringCycleCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Mise à jour cycle d'acquisition des RTT`)
            .show()
        }))
    },
  }
})

function setPageTitle(
  name :Service["name"],
  raison_sociale :Company["raison_sociale"]
) {
  document.title = join(' ', [
    `Équipe « ${name} »`,
    `— ${raison_sociale}`,
  ])
}
</script>
